<script lang="ts" setup>
import { toRef } from 'vue';
import { mdiDragHorizontal } from '@mdi/js';
import { useFieldValue, useField } from 'vee-validate';
import { warning, success, error, beamShapeCircular } from '@/components/icons/icons';
import { EvaluationState } from '@/api/generator';
import VeeCheckbox from '@/components/forms/Checkbox/VeeCheckbox.vue';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { useTranslation } from 'i18next-vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
const { t } = useTranslation();

interface EvaluationsManualTasksEnumTileParamEvaluationStateTileProps {
  name: string;
}
const mode = useFormMode();

const props = defineProps<EvaluationsManualTasksEnumTileParamEvaluationStateTileProps>();

const emits = defineEmits<{
  enabled: [enabled: boolean];
}>();

const { value: evaluationOptionName } = useFieldValue<string | undefined | null>(
  toRef(() => `${props.name}.name`),
);

const {
  errorMessage: errorMessageOptions,
  setValue: setEvaluationState,
  value: evaluationState,
} = useField<EvaluationState | undefined>(toRef(() => `${props.name}.evaluationState`));

interface EvaluationStateSelectItem {
  value: EvaluationState;
  title: string;
  icon: string;
}

const evaluationStateItems: EvaluationStateSelectItem[] = [
  {
    value: EvaluationState.Passed,
    title: t(`EvaluationStateEnum.${EvaluationState.Passed}`),
    icon: success,
  },
  {
    value: EvaluationState.Warning,
    title: t(`EvaluationStateEnum.${EvaluationState.Warning}`),
    icon: warning,
  },
  {
    value: EvaluationState.Failed,
    title: t(`EvaluationStateEnum.${EvaluationState.Failed}`),
    icon: error,
  },
  {
    value: EvaluationState.WithoutEvaluation,
    title: t(`EvaluationStateEnum.${EvaluationState.WithoutEvaluation}`),
    icon: beamShapeCircular,
  },
];

const getIconColor = (state: EvaluationState) => {
  switch (state) {
    case EvaluationState.Failed:
      return 'error-lighten-1';
    case EvaluationState.Warning:
      return 'warning-lighten-1';
    case EvaluationState.Passed:
      return 'success-lighten-1';
    default:
      return 'primary-lighten-1';
  }
};
</script>

<template>
  <div class="d-flex align-center mb-2 container" :class="mode === 'details' ? 'details' : ''">
    <v-icon v-if="mode !== 'details'" :icon="mdiDragHorizontal" class="handle cursor-move" />

    <VeeCheckbox
      :name="`${name}.isEnabled`"
      :label="evaluationOptionName ?? EMPTY_FIELD_SIGN"
      :mode="'add'"
      :disabled="mode === 'details'"
      class="cbWidth"
      @change="emits('enabled', !!$event)"
    />

    <v-select
      :error="Boolean(errorMessageOptions)"
      :persistentHint="Boolean(errorMessageOptions)"
      :items="evaluationStateItems"
      :modelValue="evaluationState"
      :disabled="mode === 'details'"
      :hint="errorMessageOptions"
      class="selectWidth"
      :errorMessage="errorMessageOptions"
      @update:modelValue="setEvaluationState"
    >
      <template #selection="{ internalItem: item }">
        <v-icon :color="getIconColor(item.props.value)" class="mr-2">{{ item.raw.icon }}</v-icon>
        {{ item.title }}
      </template>

      <template #item="{ internalItem: item, props: p }">
        <v-list-item v-bind="p" :prependIcon="item.raw.icon">
          <template #prepend>
            <v-icon :color="getIconColor(item.props.value)">{{ item.raw.icon }}</v-icon>
          </template>
        </v-list-item>
      </template>
    </v-select>
  </div>
</template>
<style scoped lang="scss">
.cbWidth {
  flex: 1 1 auto;
  width: auto !important;
}
.selectWidth {
  width: 286px;
  flex: 0 0 286px;
}
.container {
  gap: 8px;
  background-color: rgb(var(--v-theme-primary-lighten-5));
  padding: 4px 8px;
  border-radius: 4px;
}
.details {
  ::v-deep(.v-selection-control) {
    opacity: 1 !important;
  }
  ::v-deep(.v-selection-control__wrapper) {
    opacity: 0.5 !important;
  }
  ::v-deep(.v-field) {
    opacity: 1 !important;
  }
  ::v-deep(.v-field__append-inner) {
    display: none;
  }

  ::v-deep(.v-field__outline) {
    display: none;
  }
}
</style>
